/**
 * CRM row shapes — snake_case, matching the raw rows the CRM controller
 * returns. Verified against the running API before use.
 */
import type { CurrencyFigure } from "@/lib/currency";

export interface LeadRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  source: string | null;
  status: string;
  owner_user_id: string | null;
  estimated_value: string | null;
  /** The currency of estimated_value (CUR-1: every lead carries one). */
  currency_code: string;
  created_at: string;
}

export interface OpportunityRow {
  id: string;
  name: string;
  customer_id: string | null;
  stage: string;
  expected_value: string | null;
  probability_percent: number | null;
  expected_close_date: string | null;
  owner_user_id: string | null;
  /** The currency of expected_value (CUR-1: every opportunity carries one). */
  currency_code: string;
  created_at: string;
}

export interface ActivityRow {
  id: string;
  entity_type: string;
  entity_id: string;
  activity_type: string;
  note: string | null;
  occurred_at: string;
  created_by: string | null;
}

export interface CampaignRow {
  id: string;
  name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  budget: string | null;
  /** The currency of budget (CUR-1: every campaign carries one). */
  currency_code: string;
  created_at: string;
}

export interface ContactRow {
  id: string;
  party_type: string;
  party_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  created_at: string;
}

/** GET /v1/crm/opportunities/summary: open deals, never added across currencies. */
export interface OpportunitySummary {
  /** The workspace currency, the headline of `pipeline` and `weighted`. */
  currency: string;
  /** Open opportunities in every currency (a count adds across them). */
  count: number;
  pipeline: CurrencyFigure;
  /** Value × probability. */
  weighted: CurrencyFigure;
}

/** GET /v1/crm/leads/summary: open leads, never added across currencies. */
export interface LeadSummary {
  currency: string;
  count: number;
  unassigned: number;
  estimatedValue: CurrencyFigure;
}

/** Join a first and last name without leaving a stray space when one is null. */
export function personName(first: string | null, last: string | null): string {
  const name = [first, last]
    .filter((part) => part && part.trim().length > 0)
    .join(" ");
  return name.length > 0 ? name : "—";
}
